/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-sql-unique-constraint-visualizer-fr block)");
/* eslint-enable no-console */

(function () {
    // ── Données initiales ──────────────────────────────────
    const COLS_SIMPLE = ["id", "email", "nom"];
    const COLS_MULTI = ["id", "jour", "plage"];
    const COLS_NULL = ["id", "email", "nom"];

    const tabConfig = {
        s: { cls: "active-unique-s", key: "s" },
        m: { cls: "active-unique-m", key: "m" },
        n: { cls: "active-unique-n", key: "n" }
    };

    let activeQuery = null;
    let curStepIdx = -1;

    // ── Helpers ────────────────────────────────────────────
    function tableHtml(data, cols, stateMap) {
        const thead = cols.map(c => `<th>${c}</th>`).join("");
        const rows = data.map(r => {
            const cls = (stateMap && r.id !== undefined) ? (stateMap[r.id] || "") : "";
            return `<tr class="${cls}">${cols.map(c => `<td>${r[c] !== undefined ? r[c] : "NULL"}</td>`).join("")}</tr>`;
        }).join("");
        return `<table class="mini"><thead><tr>${thead}</tr></thead><tbody>${rows}</tbody></table>`;
    }

    function blockHtml(headerCls, headerLabel, content) {
        return `<div class="table-block">
                <div class="table-block-header ${headerCls}">${headerLabel}</div>
                <div class="table-block-body">${content}</div>
            </div>`;
    }

    function legendHtml(items) {
        if (!items.length) return "";
        const spans = items.map(([color, label]) =>
            `<span><i class="legend-dot" style="background:${color};border:1px solid #555;"></i> ${label}</span>`
        ).join("");
        return `<div class="legend">${spans}</div>`;
    }

    function bannerHtml(type, icon, text) {
        return `<div class="op-banner ${type}">${icon} ${text}</div>`;
    }

    // ── 1) UNIQUE simple (email unique) ─────────────────────
    const queries = {
        s: {
            parts: [
                { id: "uq-s-create", text: "CREATE TABLE utilisateurs_simple (\n  id INT,\n  email VARCHAR(100) UNIQUE,\n  nom VARCHAR(50)\n);", cls: "constraint" },
                { id: "uq-s-ins1", text: "INSERT INTO utilisateurs_simple VALUES (1, 'alice@site.com', 'Alice');", cls: "" },
                { id: "uq-s-ins2", text: "INSERT INTO utilisateurs_simple VALUES (2, 'bob@site.com', 'Bob');", cls: "" },
                { id: "uq-s-ins3", text: "INSERT INTO utilisateurs_simple VALUES (3, 'alice@site.com', 'AliceDuplicate'); -- ⚠️ échoue", cls: "danger" }
            ],
            steps: [
                {
                    key: "uq-s-create",
                    sql: "Création de la table avec UNIQUE sur email",
                    desc: "La colonne <code>email</code> est déclarée <code>UNIQUE</code> → pas de doublon autorisé.",
                    render: () => {
                        const data = [];
                        return bannerHtml("success", "✅", "Table créée avec UNIQUE sur email") +
                            blockHtml("normal", "📋 utilisateurs_simple", tableHtml(data, COLS_SIMPLE, null));
                    }
                },
                {
                    key: "uq-s-ins1",
                    sql: "INSERT — email unique 1",
                    desc: "On insère <code>alice@site.com</code> → pas de doublon → accepté.",
                    render: () => {
                        const data = [{ id: 1, email: "alice@site.com", nom: "Alice", _state: "inserted" }];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "INSERT ACCEPTÉ — email unique") +
                            blockHtml("success", "✅ utilisateurs_simple", tableHtml(data, COLS_SIMPLE, stateMap));
                    }
                },
                {
                    key: "uq-s-ins2",
                    sql: "INSERT — email unique 2",
                    desc: "On ajoute <code>bob@site.com</code> → pas de conflit → accepté.",
                    render: () => {
                        const data = [
                            { id: 1, email: "alice@site.com", nom: "Alice", _state: "normal" },
                            { id: 2, email: "bob@site.com", nom: "Bob", _state: "inserted" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "INSERT ACCEPTÉ — deux emails uniques") +
                            blockHtml("success", "✅ utilisateurs_simple", tableHtml(data, COLS_SIMPLE, stateMap));
                    }
                },
                {
                    key: "uq-s-ins3",
                    sql: "INSERT — doublon email → échec",
                    desc: "On réutilise <code>alice@site.com</code>. La contrainte <code>UNIQUE</code> est violée → rejet.",
                    render: () => {
                        const data = [
                            { id: 1, email: "alice@site.com", nom: "Alice", _state: "normal" },
                            { id: 2, email: "bob@site.com", nom: "Bob", _state: "normal" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("failure", "❌", "INSERT REJETÉ — doublon email (UNIQUE violée)") +
                            blockHtml("failure", "📋 utilisateurs_simple — inchangée", tableHtml(data, COLS_SIMPLE, stateMap)) +
                            `<p style="font-size:11px;color:#7f1d1d;"><strong>❌ Résultat :</strong> pas de nouvelle ligne ajoutée.</p>`;
                    }
                }
            ]
        },

        // ── 2) UNIQUE multicols (jour, plage) ─────────────────
        // ── 2) UNIQUE multicols (jour, plage) ─────────────────
        m: {
            parts: [
                { id: "uq-m-create", text: "CREATE TABLE ouvertures (\n  id INT,\n  jour VARCHAR(10),\n  plage VARCHAR(20),\n  UNIQUE (jour, plage)\n);", cls: "constraint" },
                { id: "uq-m-ins1", text: "INSERT INTO ouvertures VALUES (1, 'Lundi', '09h-12h');", cls: "" },
                { id: "uq-m-ins2", text: "INSERT INTO ouvertures VALUES (2, 'Lundi', '13h-17h');", cls: "" },
                { id: "uq-m-ins3", text: "INSERT INTO ouvertures VALUES (3, 'Lundi', '09h-12h'); -- ⚠️ échoue", cls: "danger" }
            ],
            steps: [
                {
                    key: "uq-m-create",
                    sql: "Création de la table avec UNIQUE (jour, plage)",
                    desc: "Le couple <code>(jour, plage)</code> doit être unique → pas de doublon de creneau.",
                    render: () => {
                        const data = [];
                        return bannerHtml("success", "✅", "Table créée avec UNIQUE (jour, plage)") +
                            blockHtml("normal", "📋 ouvertures — creneaux horaires uniques", tableHtml(data, COLS_MULTI, null));
                    }
                },
                {
                    key: "uq-m-ins1",
                    sql: "INSERT — creneau unique 1",
                    desc: "Creneau <code>('Lundi','09h-12h')</code> → accepté.",
                    render: () => {
                        const data = [{ id: 1, jour: "Lundi", plage: "09h-12h", _state: "inserted" }];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "Creneau Lundi 09h-12h — accepté") +
                            blockHtml("success", "✅ ouvertures", tableHtml(data, COLS_MULTI, stateMap));
                    }
                },
                {
                    key: "uq-m-ins2",
                    sql: "INSERT — autre creneau pour Lundi",
                    desc: "Creneau <code>('Lundi','13h-17h')</code> → différent du premier → accepté.",
                    render: () => {
                        const data = [
                            { id: 1, jour: "Lundi", plage: "09h-12h", _state: "normal" },
                            { id: 2, jour: "Lundi", plage: "13h-17h", _state: "inserted" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "Creneau Lundi 13h-17h — accepté") +
                            blockHtml("success", "✅ ouvertures", tableHtml(data, COLS_MULTI, stateMap));
                    }
                },
                {
                    key: "uq-m-ins3",
                    sql: "INSERT — même creneau → échec",
                    desc: "On tente de réutiliser le couple <code>('Lundi','09h-12h')</code>. La contrainte <code>UNIQUE (jour, plage)</code> est violée.",
                    render: () => {
                        const data = [
                            { id: 1, jour: "Lundi", plage: "09h-12h", _state: "normal" },
                            { id: 2, jour: "Lundi", plage: "13h-17h", _state: "normal" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("failure", "❌", "INSERT REJETÉ — doublon de creneau (UNIQUE multicols violée)") +
                            blockHtml("failure", "📋 ouvertures — inchangées", tableHtml(data, COLS_MULTI, stateMap)) +
                            `<p style="font-size:11px;color:#7f1d1d;"><strong>❌ Résultat :</strong> pas de nouvelle ligne ajoutée.</p>`;
                    }
                }
            ]
        },


        // ── 3) UNIQUE + NULL (email nullable) ────────────────
        n: {
            parts: [
                { id: "uq-n-create", text: "CREATE TABLE utilisateurs_null (\n  id INT,\n  email VARCHAR(100) UNIQUE,\n  nom VARCHAR(50)\n);", cls: "constraint" },
                { id: "uq-n-ins1", text: "INSERT INTO utilisateurs_null VALUES (1, 'alice@site.com', 'Alice');", cls: "" },
                { id: "uq-n-ins2", text: "INSERT INTO utilisateurs_null VALUES (2, NULL, 'Bob');", cls: "" },
                { id: "uq-n-ins3", text: "INSERT INTO utilisateurs_null VALUES (3, NULL, 'Claire'); -- ⚠️ échoue", cls: "danger" }
            ],
            steps: [
                {
                    key: "uq-n-create",
                    sql: "Création de la table avec UNIQUE sur email (nullable)",
                    desc: "La colonne <code>email</code> est UNIQUE mais peut contenir <code>NULL</code>.",
                    render: () => {
                        const data = [];
                        return bannerHtml("success", "✅", "Table créée UNIQUE(email) nullable") +
                            blockHtml("normal", "📋 utilisateurs_null", tableHtml(data, COLS_NULL, null));
                    }
                },
                {
                    key: "uq-n-ins1",
                    sql: "INSERT — email non NULL",
                    desc: "On insère <code>alice@site.com</code> → accepté.",
                    render: () => {
                        const data = [{ id: 1, email: "alice@site.com", nom: "Alice", _state: "inserted" }];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "INSERT ACCEPTÉ — email non NULL") +
                            blockHtml("success", "✅ utilisateurs_null", tableHtml(data, COLS_NULL, stateMap));
                    }
                },
                {
                    key: "uq-n-ins2",
                    sql: "INSERT — email NULL",
                    desc: "On autorise <code>NULL</code> pour un utilisateur (pas d'email).",
                    render: () => {
                        const data = [
                            { id: 1, email: "alice@site.com", nom: "Alice", _state: "normal" },
                            { id: 2, email: null, nom: "Bob", _state: "inserted" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("success", "✅", "INSERT ACCEPTÉ — email NULL autorisé") +
                            blockHtml("success", "✅ utilisateurs_null", tableHtml(data, COLS_NULL, stateMap));
                    }
                },
                {
                    key: "uq-n-ins3",
                    sql: "INSERT — email NULL + email NULL → échec",
                    desc: "On tente de stocker un autre <code>NULL</code>. Sur certains SGBD, UNIQUE interdit plusieurs <code>NULL</code> → échec.",
                    render: () => {
                        const data = [
                            { id: 1, email: "alice@site.com", nom: "Alice", _state: "normal" },
                            { id: 2, email: null, nom: "Bob", _state: "normal" }
                        ];
                        const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                        return bannerHtml("failure", "⚠️", "INSERT REJETÉ — doublon possible interprété sur NULL (selon SGBD)") +
                            blockHtml("failure", "📋 utilisateurs_null — inchangées", tableHtml(data, COLS_NULL, stateMap)) +
                            `<p style="font-size:11px;color:#7f1d1d;"><strong>⚠️ Interprétation SGBD :</strong> certains moteurs interdisent plusieurs NULL avec UNIQUE.</p>`;
                    }
                }
            ]
        }
    };

    // ── Init tab et query ─────────────────────────────────
    function setActiveTab(key) {
        Object.keys(tabConfig).forEach(k => {
            const el = document.getElementById(`tab-unique-${k}`);
            if (k === key) {
                el.className = `query-tab ${tabConfig[k].cls}`;
                activeQuery = queries[k];
            } else {
                el.className = "query-tab";
            }
        });
        curStepIdx = -1;
        renderSql();
        reset();
    }

    // ── Rendu SQL ──────────────────────────────────────────
    function renderSql() {
        document.getElementById("sql-display").innerHTML =
            activeQuery.parts.map(p =>
                `<span class="sql-part ${p.cls || ""}" id="${p.id}">${p.text}</span>`
            ).join("\n");
    }

    // ── Mise à jour UI ─────────────────────────────────────
    function updateStep(i) {
        curStepIdx = i;
        const step = activeQuery.steps[i];

        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        const el = document.getElementById(step.key);
        if (el) el.classList.add("active");

        document.getElementById("step-meta").innerHTML =
            `<strong>Étape ${i + 1} / ${activeQuery.steps.length} — ${step.sql}</strong><br>${step.desc}`;

        document.getElementById("step-output").innerHTML = step.render();
    }

    // ── Reset ──────────────────────────────────────────────
    function reset() {
        curStepIdx = -1;
        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        document.getElementById("step-meta").textContent =
            "Cliquez sur un bouton pour avancer ou reculer.";
        document.getElementById("step-output").innerHTML = "";
    }

    // ── Navigation ─────────────────────────────────────────
    document.getElementById("btn-next").addEventListener("click", () => {
        if (curStepIdx < activeQuery.steps.length - 1) updateStep(curStepIdx + 1);
    });

    document.getElementById("btn-prev").addEventListener("click", () => {
        if (curStepIdx > 0) updateStep(curStepIdx - 1);
    });

    document.getElementById("btn-reset").addEventListener("click", reset);

    // ── Bascule onglets UNIQUE ─────────────────────────────
    Object.keys(tabConfig).forEach(key => {
        document.getElementById(`tab-unique-${key}`).addEventListener("click", () => {
            setActiveTab(key);
        });
    });

    // ── Init ───────────────────────────────────────────────
    setActiveTab("s");
})();